import React, { useEffect, useState } from "react";
import FsLightbox from "fslightbox-react";
import Modal from "react-modal";
import { API_URL } from "../../api/APIUrl";

const AdminPostList = ({ visible }) => {
  const [toggler, setToggler] = useState(false);
  const [tempUrl, setTempUrl] = useState(null);
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [togglerArray, setTogglerArray] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [pdfModalIsOpen, setPdfModalIsOpen] = useState(false);
  const [pdfTarget, setPdfTarget] = useState(null);
  const [newPdfFile, setNewPdfFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Consistent view limits to control height snapping
  const itemsPerPage = 10;

  const toggleLightbox = (index) => {
    const newTogglerArray = [...togglerArray];
    newTogglerArray[index] = !newTogglerArray[index];
    setTogglerArray(newTogglerArray);
  };

  const openModal = (post) => {
    setEditPost(post);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setEditPost(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditPost({ ...editPost, [name]: value });
  };

  const handlePdfReplace = async (e) => {
    e.preventDefault();
    if (!newPdfFile) return alert("Please select a PDF file.");

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", newPdfFile);

    try {
      const response = await fetch(
        `${API_URL}/api/materials/${pdfTarget._id}/update-pdf`,
        {
          method: "PUT",
          body: formData,
        }
      );

      if (response.ok) {
        alert("PDF replaced successfully!");
        setPdfModalIsOpen(false);
        setNewPdfFile(null);
        getAllPosts();
      }
    } catch (error) {
      console.error("Upload Error:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    fetch(`${API_URL}/api/materials/${editPost._id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(editPost),
    })
      .then((r) => r.json())
      .then((data) => {
        getAllPosts();
        closeModal();
      });
  };

  function getAllPosts() {
    fetch(`${API_URL}/api/materials`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((r) => r.json())
      .then((data) => {
        const fetchedPosts = data.data.reverse();
        setPosts(fetchedPosts);
        setTogglerArray(Array(fetchedPosts.length).fill(false));
      });
  }

  useEffect(() => {
    getAllPosts();
  }, []);

  const viewPDF = async (id) => {
    const rawUrl = await generateMaterialUrl(id);
    if (rawUrl) {
      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        rawUrl
      )}&embedded=true`;
      setTempUrl(viewerUrl);
      setToggler(!toggler);
    }
  };

  const generateMaterialUrl = async (id) => {
    const res = await fetch(`${API_URL}/api/materials/${id}/download`);
    const data = await res.json();
    return data.url;
  };

  // --- DYNAMIC SEARCH & CATEGORY FILTER LOGIC ---
  const filteredPosts = React.useMemo(() => {
    return posts.filter((post) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        post.postTitle?.toLowerCase().includes(searchLower) ||
        post.postDesc?.toLowerCase().includes(searchLower);

      const matchesCategory =
        categoryFilter === "" || post.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [posts, searchTerm, categoryFilter]);

  // Reset pagination to page 1 on active search/filtering changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter]);

  const finalIndex = currentPage * itemsPerPage;
  const startingIndex = finalIndex - itemsPerPage;
  const npage = Math.ceil(filteredPosts.length / itemsPerPage);

  // --- SORT LOGIC (Applied to Filtered List) ---
  const sortedItems = React.useMemo(() => {
    let sortableItems = [...filteredPosts];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const keyA = a[sortConfig.key] || "";
        const keyB = b[sortConfig.key] || "";
        if (keyA < keyB) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (keyA > keyB) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems.slice(startingIndex, finalIndex);
  }, [filteredPosts, sortConfig, startingIndex, finalIndex]);

  // Calculates how many placeholder empty rows are needed to reach maximum uniform pagination bounds
  const emptyRowsCount = itemsPerPage - sortedItems.length;

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  function prePage() {
    if (currentPage !== 1) setCurrentPage(currentPage - 1);
  }
  function changeCPage(id) {
    setCurrentPage(id);
  }
  function nextPage() {
    if (currentPage !== npage) setCurrentPage(currentPage + 1);
  }

  function getPageRange(current, total) {
    const range = [];
    if (total <= 6) {
      for (let i = 1; i <= total; i++) range.push(i);
    } else {
      range.push(1);
      if (current > 4) range.push("...");
      if (current > 3 && current < total - 2) {
        range.push(current - 1, current, current + 1);
      } else if (current <= 3) {
        range.push(2, 3, 4);
      } else {
        range.push(total - 3, total - 2, total - 1);
      }
      if (current < total - 3) range.push("...");
      range.push(total);
    }
    return range;
  }

  // --- UI DESIGN STYLING CARDS ---
  const layoutContainerStyle = {
    display: visible === false ? "none" : "flex",
    flexDirection: "column",
    width: "100%",
    height: "calc(100vh - 140px)", // Bounds container to window space to block global scrolls
    boxSizing: "border-box",
    padding: "10px 0px 20px 0px",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  };

  const headerContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexShrink: 0,
  };

  const titleStyle = {
    margin: 0,
    fontSize: "24px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "-0.5px",
  };

  const addBtnStyle = {
    backgroundColor: "#007bff",
    color: "#ffffff",
    border: "none",
    borderRadius: "10px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0, 123, 255, 0.15)",
    textDecoration: "none",
    display: "inline-block",
  };

  const controlsStripStyle = {
    display: "flex",
    gap: "16px",
    marginBottom: "16px",
    flexShrink: 0,
  };

  const inputControlStyle = {
    padding: "10px 16px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    color: "#0f172a",
    outline: "none",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.01)",
  };

  const tableCardFrameStyle = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    border: "1px solid #eef2f6",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.02)",
    overflow: "hidden",
    flexGrow: 1, // Allows framework to scale seamlessly within viewport limits
    minHeight: 0,
  };

  const tableScrollBodyStyle = {
    overflowY: "auto", // Traps tall datasets safely within an internal scroll frame
    flexGrow: 1,
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
  };

  const thStyle = (sortKey) => ({
    backgroundColor: "#f8fafc",
    color: "#64748b",
    fontWeight: "700",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "14px 24px",
    textAlign: "left",
    borderBottom: "1px solid #eef2f6",
    cursor: sortKey ? "pointer" : "default",
    userSelect: "none",
    position: "sticky", // Locks table headers safely while scrolling internal content
    top: 0,
    zIndex: 10,
  });

  const tdStyle = {
    padding: "10px 24px",
    fontSize: "14px",
    color: "#334155",
    borderBottom: "1px solid #eef2f6",
    textAlign: "left",
    height: "46px", // Scaled down cleanly to guarantee safe compact screen rendering
    boxSizing: "border-box",
  };

  const actionBtnStyle = (type) => {
    let bg = "#f1f5f9";
    let color = "#475569";
    if (type === "primary") {
      bg = "#007bff";
      color = "#ffffff";
    }
    return {
      backgroundColor: bg,
      color: color,
      border: "none",
      borderRadius: "6px",
      padding: "5px 12px",
      fontSize: "12px",
      fontWeight: "600",
      cursor: "pointer",
      marginRight: "6px",
      transition: "all 0.2s ease",
    };
  };

  const paginationStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    padding: "14px 24px",
    backgroundColor: "#ffffff",
    borderTop: "1px solid #eef2f6",
    flexShrink: 0,
  };

  const pageBtnStyle = (isActive) => ({
    backgroundColor: isActive ? "#007bff" : "#ffffff",
    color: isActive ? "#ffffff" : "#475569",
    border: isActive ? "1px solid #007bff" : "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "5px 12px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  });

  const modalOverlayStyles = {
    overlay: {
      backgroundColor: "rgba(15, 23, 42, 0.4)",
      backdropFilter: "blur(4px)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      position: "relative",
      width: "100%",
      maxWidth: "500px",
      inset: "auto",
      background: "#ffffff",
      borderRadius: "16px",
      padding: "32px",
      border: "none",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
    },
  };

  const modalTitleStyle = {
    margin: "0 0 24px 0",
    fontSize: "20px",
    fontWeight: "800",
    color: "#0f172a",
  };
  const formGroupStyle = {
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    textAlign: "left",
  };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#475569" };
  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "15px",
    boxSizing: "border-box",
  };
  const submitBtnStyle = {
    backgroundColor: "#007bff",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    padding: "12px",
    fontWeight: "600",
    cursor: "pointer",
    flexGrow: 1,
  };
  const cancelBtnStyle = {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "none",
    borderRadius: "8px",
    padding: "12px",
    fontWeight: "600",
    cursor: "pointer",
    flexGrow: 1,
  };

  if (!visible) return null;

  return (
    <div style={layoutContainerStyle}>
      {/* Title Header Block */}
      <div style={headerContainerStyle}>
        <h1 style={titleStyle}>Marketing Materials</h1>
        <a href="/add-post" style={addBtnStyle}>
          Add Material
        </a>
      </div>

      {/* Modern Search and Category Filters Strip */}
      <div style={controlsStripStyle}>
        <input
          type="text"
          placeholder="Search materials by title or details..."
          style={{ ...inputControlStyle, flexGrow: 2 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          style={{ ...inputControlStyle, flexGrow: 1, minWidth: "200px" }}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          <option value="specials flyers">Specials Flyer</option>
          <option value="informational flyers">Informational Flyer</option>
          <option value="course flyers">Course Flyer</option>
          <option value="brochures">Brochure</option>
          <option value="catalogs">Catalog</option>
          <option value="tabloids">Tabloid</option>
          <option value="forms">Form</option>
          <option value="solutions">Solution</option>
        </select>
      </div>

      {/* MAIN DATA BLOCK WRAPPER FRAME */}
      <div style={tableCardFrameStyle}>
        <div style={tableScrollBodyStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th
                  style={thStyle("postTitle")}
                  onClick={() => requestSort("postTitle")}
                >
                  Material Title{" "}
                  {sortConfig.key === "postTitle"
                    ? sortConfig.direction === "ascending"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  style={thStyle("category")}
                  onClick={() => requestSort("category")}
                >
                  Category{" "}
                  {sortConfig.key === "category"
                    ? sortConfig.direction === "ascending"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th
                  style={thStyle("postDesc")}
                  onClick={() => requestSort("postDesc")}
                >
                  Description{" "}
                  {sortConfig.key === "postDesc"
                    ? sortConfig.direction === "ascending"
                      ? "▲"
                      : "▼"
                    : ""}
                </th>
                <th style={thStyle(null)}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.length > 0 ? (
                <>
                  {/* Active Content Rows */}
                  {sortedItems.map((post, index) => (
                    <tr
                      key={post._id || index}
                      style={{ backgroundColor: "#ffffff" }}
                    >
                      <td style={tdStyle} data-label="Material Title">
                        <span style={{ fontWeight: "600", color: "#0f172a" }}>
                          {post.postTitle}
                        </span>
                      </td>
                      <td style={tdStyle} data-label="Category">
                        <span
                          style={{
                            fontSize: "12px",
                            textTransform: "uppercase",
                            backgroundColor: "#f1f5f9",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontWeight: "600",
                            color: "#64748b",
                          }}
                        >
                          {post.category}
                        </span>
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          color: "#64748b",
                          maxWidth: "300px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        data-label="Description"
                      >
                        {post.postDesc}
                      </td>
                      <td style={tdStyle} data-label="Action">
                        <button
                          style={actionBtnStyle("secondary")}
                          onClick={() => openModal(post)}
                        >
                          Edit Text
                        </button>

                        <FsLightbox
                          toggler={togglerArray[startingIndex + index]}
                          sources={[post.thumbnail]}
                        />

                        <button
                          style={actionBtnStyle("primary")}
                          onClick={() => {
                            setPdfTarget(post);
                            setPdfModalIsOpen(true);
                          }}
                        >
                          Replace PDF
                        </button>
                        <button
                          style={actionBtnStyle("primary")}
                          onClick={() => viewPDF(post._id)}
                        >
                          View PDF
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* Clean Visual Filler Spacers to eliminate card frame shrinking gaps */}
                  {emptyRowsCount > 0 &&
                    Array.from({ length: emptyRowsCount }).map((_, index) => (
                      <tr
                        key={`empty-${index}`}
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <td style={tdStyle}>&nbsp;</td>
                        <td style={tdStyle}>&nbsp;</td>
                        <td style={tdStyle}>&nbsp;</td>
                        <td style={tdStyle}>&nbsp;</td>
                      </tr>
                    ))}
                </>
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    style={{
                      ...tdStyle,
                      textAlign: "center",
                      color: "#94a3b8",
                      padding: "40px",
                      height: "auto",
                    }}
                  >
                    No active records matched your current criteria settings.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* LOCKED NAVIGATION BAR FOOTER PANEL */}
        {npage > 0 && (
          <div style={paginationStyle}>
            <button
              onClick={prePage}
              disabled={currentPage === 1}
              style={{
                ...pageBtnStyle(false),
                opacity: currentPage === 1 ? 0.4 : 1,
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>
            {getPageRange(currentPage, npage).map((page, index) =>
              page === "..." ? (
                <span
                  key={index}
                  style={{
                    color: "#94a3b8",
                    padding: "0 4px",
                    fontWeight: "600",
                  }}
                >
                  ...
                </span>
              ) : (
                <button
                  key={index}
                  onClick={() => changeCPage(page)}
                  style={pageBtnStyle(currentPage === page)}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={nextPage}
              disabled={currentPage === npage}
              style={{
                ...pageBtnStyle(false),
                opacity: currentPage === npage ? 0.4 : 1,
                cursor: currentPage === npage ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* MODAL 1: EDIT TEXT DETAILS */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={modalOverlayStyles}
      >
        <h3 style={modalTitleStyle}>Edit Material Post</h3>
        {editPost && (
          <form onSubmit={handleSave}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Title</label>
              <input
                type="text"
                style={inputStyle}
                name="postTitle"
                value={editPost.postTitle}
                onChange={handleInputChange}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Description</label>
              <input
                type="text"
                style={inputStyle}
                name="postDesc"
                value={editPost.postDesc}
                onChange={handleInputChange}
                required
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Category</label>
              <select
                style={{ ...inputStyle, appearance: "none" }}
                name="category"
                value={editPost.category}
                onChange={handleInputChange}
                required
              >
                <option value="">Please choose a category*</option>
                <option value="specials flyers">Specials Flyer</option>
                <option value="informational flyers">
                  Informational Flyer
                </option>
                <option value="course flyers">Course Flyer</option>
                <option value="brochures">Brochure</option>
                <option value="catalogs">Catalog</option>
                <option value="tabloids">Tabloid</option>
                <option value="forms">Form</option>
                <option value="solutions">Solution</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
              <button style={submitBtnStyle} type="submit">
                Update Details
              </button>
              <button style={cancelBtnStyle} type="button" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* MODAL 2: REPLACE PDF FILE */}
      <Modal
        isOpen={pdfModalIsOpen}
        onRequestClose={() => setPdfModalIsOpen(false)}
        style={modalOverlayStyles}
      >
        <h3 style={modalTitleStyle}>Replace Document Asset</h3>
        <p
          style={{
            margin: "-12px 0 20px 0",
            fontSize: "14px",
            color: "#64748b",
          }}
        >
          Target File:{" "}
          <strong style={{ color: "#0f172a" }}>{pdfTarget?.postTitle}</strong>
        </p>

        <form onSubmit={handlePdfReplace}>
          <div
            style={{
              ...formGroupStyle,
              border: "2px dashed #cbd5e1",
              borderRadius: "12px",
              padding: "24px",
              backgroundColor: "#f8fafc",
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            <input
              type="file"
              accept=".pdf"
              style={{ fontSize: "14px", color: "#475569" }}
              onChange={(e) => setNewPdfFile(e.target.files[0])}
              required
            />
          </div>

          <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
            <button
              style={{
                ...submitBtnStyle,
                backgroundColor: isUploading ? "#94a3b8" : "#007bff",
              }}
              type="submit"
              disabled={isUploading}
            >
              {isUploading ? "Uploading File..." : "Upload & Overwrite"}
            </button>
            <button
              style={cancelBtnStyle}
              type="button"
              onClick={() => setPdfModalIsOpen(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Lightbox iframe container renderer utility */}
      <FsLightbox
        toggler={toggler}
        sources={[
          <iframe
            src={tempUrl}
            width="100%"
            height="750px"
            key={tempUrl}
            style={{ border: "none", borderRadius: "8px" }}
          />,
        ]}
      />
    </div>
  );
};

export default AdminPostList;
